/**
 * Profile service — all /UserProfile API calls.
 */
import userProfileApi from '../userProfile/userProfileApi';
import {
  PROFILE_TABS,
  PROFILE_TABS_PUBLIC,
} from '../../constants';
import { toRelativeMediaPath } from '../../utils/imageUrl';
import { buildFilePart } from '../../platform/filePart';
import {
  UserProfileUpsertRequest,
  UserProfileUpdateRequest,
  ChartDataRequest,
  UpdateMeasurementRequest,
  UserRelationRequest,
  InviteRequest,
  GetUserConnectionsRequest,
  BuddySearchRequest,
  AccountSettingsUpdateRequest,
  LeaderBoardListRequest,
  ReportReason,
  REPORT_REASON_OPTIONS,
} from '../../types';

const LOCAL_IMAGE_SCHEMES = ['file:', 'content:', 'ph:', 'assets-library:', 'data:'];

const isNewProfilePicture = (uri: string, blob?: Blob | null): boolean => {
  if (blob) return true;
  const normalized = uri.trim().toLowerCase();
  return LOCAL_IMAGE_SCHEMES.some(scheme => normalized.startsWith(scheme));
};

// ─── API Calls ────────────────────────────────────────────────────────────────

/** Returns the authenticated user's full profile. */
const getProfile = () =>
  userProfileApi.getProfile();

/** Updates the authenticated user's profile. */
const updateProfile = (payload: UserProfileUpdateRequest) => {
  const formData = new FormData();

  if (payload.firstName !== undefined) formData.append('FirstName', payload.firstName);
  if (payload.lastName !== undefined) formData.append('LastName', payload.lastName);
  if (payload.genderId !== undefined) formData.append('GenderId', String(payload.genderId));
  if (payload.countryId !== undefined) formData.append('CountryId', String(payload.countryId));
  if (payload.heightCm !== undefined) formData.append('HeightCm', String(payload.heightCm));
  if (payload.heightScaleUnit !== undefined) formData.append('HeightScaleUnit', String(payload.heightScaleUnit));
  if (payload.weightKg !== undefined) formData.append('WeightKg', String(payload.weightKg));
  if (payload.weightScaleUnit !== undefined) formData.append('WeightScaleUnit', String(payload.weightScaleUnit));
  if (payload.dateOfBirth !== undefined) formData.append('DateOfBirth', payload.dateOfBirth);

  if (payload.profilePicture) {
    // A freshly picked file is a local URI (native) or an object URL backed by
    // the picked Blob (web). Anything else is an already stored path: on web the
    // image base is relative, so "not http" alone would send it to buildFilePart,
    // which throws without a Blob and aborts the whole update.
    if (isNewProfilePicture(payload.profilePicture, payload.profilePictureBlob)) {
      const uri = payload.profilePicture;
      let filename = uri.split('/').pop() || 'profile.jpg';
      if (!filename.includes('.')) filename = 'profile.jpg';
      const type = filename.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
      formData.append(
        'ProfilePicture',
        buildFilePart({ uri, name: filename, type, blob: payload.profilePictureBlob }),
      );
    } else {
      const relativePath = toRelativeMediaPath(payload.profilePicture);
      if (relativePath) {
        const formattedPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
        formData.append('ProfilePicture', formattedPath);
      }
    }
  }

  if (payload.aboutMe !== undefined) formData.append('AboutMe', payload.aboutMe);
  if (payload.bio !== undefined) formData.append('Bio', payload.bio);
  if (payload.profileViewPrivacy !== undefined) formData.append('ProfileViewPrivacy', String(payload.profileViewPrivacy));
  if (payload.weightPrivacy !== undefined) formData.append('WeightPrivacy', String(payload.weightPrivacy));
  if (payload.heightPrivacy !== undefined) formData.append('HeightPrivacy', String(payload.heightPrivacy));
  if (payload.countryPrivacy !== undefined) formData.append('CountryPrivacy', String(payload.countryPrivacy));
  if (payload.bioPrivacy !== undefined) formData.append('BioPrivacy', String(payload.bioPrivacy));
  if (payload.aboutMePrivacy !== undefined) formData.append('AboutMePrivacy', String(payload.aboutMePrivacy));
  if (payload.dateOfBirthPrivacy !== undefined) formData.append('DateOfBirthPrivacy', String(payload.dateOfBirthPrivacy));
  if (payload.genderPrivacy !== undefined) formData.append('GenderPrivacy', String(payload.genderPrivacy));
  if (payload.profilePicturePrivacy !== undefined) formData.append('ProfilePicturePrivacy', String(payload.profilePicturePrivacy));

  return userProfileApi.updateProfilePrivacySettings(formData);
};

/** Insert or update a profile record (typically used after registration). */
const upsertProfile = (payload: UserProfileUpsertRequest) =>
  userProfileApi.upsertProfile(payload);

/** Returns lightweight user info decoded from the bearer token claims. */
const getUserInfo = () =>
  userProfileApi.getUserInfo();

/** Fetches another user's profile details. */
const getOtherUserProfile = (profileUserGuid: string) =>
  userProfileApi.getOtherUserProfile(profileUserGuid);

/** Returns weight chart data for the given date range and period. */
const getChartData = (payload: ChartDataRequest) =>
  userProfileApi.getChartData(payload);

/** Logs a new weight measurement for the authenticated user. */
const updateMeasurement = (payload: UpdateMeasurementRequest) =>
  userProfileApi.updateMeasurement(payload);

/** Performs a social action (Follow, UnFollow, Block, UnBlock, Agent) against another user. */
const userRelation = (payload: UserRelationRequest) => {
  return userProfileApi.userRelation({
    relatedUserId: payload.relatedUserId,
    action: payload.action,
  });
};

export type BlockRelationResult = {
  success: boolean;
  relatedUserId: number | string;
  action: 'block' | 'unblock';
  message?: string;
};

/**
 * Shared Block / Unblock helper for UserProfile/UserRelation.
 * Always sends `{ relatedUserId, action: "block" | "unblock" }` exactly once.
 */
const setBlockRelation = async (
  relatedUserId: number | string | null | undefined,
  shouldBlock: boolean,
): Promise<BlockRelationResult> => {
  const action: 'block' | 'unblock' = shouldBlock ? 'block' : 'unblock';

  if (!relatedUserId || (typeof relatedUserId === 'number' && (!Number.isFinite(relatedUserId) || relatedUserId <= 0))) {
    return {
      success: false,
      relatedUserId: relatedUserId ?? 0,
      action,
      message: 'Invalid user ID',
    };
  }

  try {
    const response = await userRelation({
      relatedUserId,
      action,
    });
    const ok =
      response.data?.success !== false &&
      (response.status === 200 || response.status === 201);

    return {
      success: ok,
      relatedUserId,
      action,
      message: response.data?.message,
    };
  } catch (err: any) {
    return {
      success: false,
      relatedUserId,
      action,
      message:
        err?.response?.data?.message || err?.message || 'Failed to update block status',
    };
  }
};

/** Accepts or declines a buddy invite from another user. */
const updateInvite = (payload: InviteRequest) =>
  userProfileApi.updateInvite(payload);

/** Returns a paginated list of followers, following, or buddies. */
const getUserConnections = (payload: GetUserConnectionsRequest, signal?: AbortSignal) =>
  userProfileApi.getUserConnections(payload, signal);

/** Fetches the list of users blocked by the authenticated user. */
const getBlockedUsers = () =>
  userProfileApi.getBlockedUsers();

/** Searches for matching buddies using filter criteria. POST /api/v1/UserProfile/BuddySearch */
const buddySearch = (payload: BuddySearchRequest) =>
  userProfileApi.buddySearch(payload);

const getAccountSettings = () =>
  userProfileApi.getAccountSettings();

const updateAccountSettings = (payload: AccountSettingsUpdateRequest) =>
  userProfileApi.updateAccountSettings(payload);

const getLeaderBoardList = (payload: LeaderBoardListRequest) =>
  userProfileApi.getLeaderBoardList(payload);


// ─── Local Static Data (no backend endpoint) ──────────────────────────────────

/** Returns local profile tab labels (no backend endpoint). */
const getProfileTabs = async () => ({ data: [...PROFILE_TABS] });

/** Returns local buddy-profile tab labels (no backend endpoint). */
const getBuddyProfileTabs = async () => ({ data: [...PROFILE_TABS_PUBLIC] });

const getReportReasons = async (): Promise<ReportReason[]> => REPORT_REASON_OPTIONS;


// ─── Export ───────────────────────────────────────────────────────────────────

const profileService = {
  // API-backed
  getProfile,
  updateProfile,
  upsertProfile,
  getUserInfo,
  getOtherUserProfile,
  getChartData,
  updateMeasurement,
  userRelation,
  setBlockRelation,
  updateInvite,
  getUserConnections,
  getBlockedUsers,
  buddySearch,
  getAccountSettings,
  updateAccountSettings,
  getLeaderBoardList,
  getAwards: userProfileApi.getAwards,
  reportUser: userProfileApi.reportUser,
  getReportReasonOptions: () => REPORT_REASON_OPTIONS,
  getUserBuddyPreferences: userProfileApi.getUserBuddyPreferences,
  buddyMatching: userProfileApi.buddyMatching,
  // Local static
  getProfileTabs,
  getBuddyProfileTabs,
  getReportReasons,
};

export default profileService;
